import React from 'react';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../../../components/ui/dialog';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import { Loader2, CalendarCheck, Scissors, Ban, Clock, DollarSign } from 'lucide-react';
import { fmtDate, fmtWindow, hoursLabel, money } from '../../../lib/scheduleFormat';

const TAGS = {
  booked: { label: 'Book', done: 'Booked', className: 'border-emerald-300 bg-emerald-50 text-emerald-800', Icon: CalendarCheck },
  trimmed: { label: 'Trim to fit', done: 'Trimmed to fit', className: 'border-amber-300 bg-amber-50 text-amber-900', Icon: Scissors },
  skipped: { label: 'Skip', done: 'Skipped', className: 'border-slate-300 bg-slate-50 text-slate-700', Icon: Ban },
};

const splitWindow = (window) => (window || '').split('–');

// Hours cost money, so every run — preview or result — shows the hours and the
// cost before anything is confirmed, and explains each trim and skip in words.
const HoursRunDialog = ({ open, run, title, description, confirmLabel, busy, onConfirm, onClose }) => {
  const committed = Boolean(run?.committed);
  const rows = [
    ...(run?.booked || []).map((row) => ({ ...row, kind: 'booked' })),
    ...(run?.trimmed || []).map((row) => ({ ...row, kind: 'trimmed' })),
    ...(run?.skipped || []).map((row) => ({ ...row, kind: 'skipped' })),
  ].sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : 0));
  const totals = run?.totals || {};

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="hours-run-dialog">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div
            className="flex flex-wrap items-center gap-x-4 gap-y-2 rounded-lg border border-amber-200 bg-amber-50 p-3"
            data-testid="hours-run-totals"
          >
            <span className="text-sm font-medium text-gray-900">
              {totals.bookings || 0} day{totals.bookings === 1 ? '' : 's'}
            </span>
            <span className="flex items-center gap-1 text-sm font-medium text-gray-900">
              <Clock className="h-4 w-4 text-amber-700" /> {hoursLabel(totals.hours)}
            </span>
            <span className="flex items-center gap-1 text-sm font-medium text-gray-900" data-testid="hours-run-cost">
              <DollarSign className="h-4 w-4 text-amber-700" /> {money(totals.cost)}
            </span>
            <span className="text-xs text-gray-600">
              {committed ? 'hours now booked' : 'hours you would be paying for'}
              {run?.start && run?.end ? ` · ${fmtDate(run.start)} – ${fmtDate(run.end)}` : ''}
            </span>
          </div>

          {rows.length === 0 && (
            <p className="rounded-lg border border-gray-200 bg-gray-50 p-3 text-sm text-gray-600"
              data-testid="hours-run-empty">
              Nothing to do — every day in range is either already booked or closed.
            </p>
          )}

          <div className="space-y-1.5">
            {rows.map((row, index) => {
              const tag = TAGS[row.kind];
              const [from, to] = splitWindow(row.window);
              return (
                <div
                  key={`${row.date}-${row.kind}-${index}`}
                  className="flex flex-wrap items-start justify-between gap-2 rounded-lg border border-gray-200 p-2.5"
                  data-testid={`hours-run-row-${row.date}-${row.kind}`}
                >
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-gray-900">
                      {fmtDate(row.date)}
                      <span className="ml-2 font-normal text-gray-700">
                        {row.kind === 'skipped' ? '' : fmtWindow(from, to)}
                      </span>
                    </p>
                    {row.reason && (
                      <p className="mt-0.5 text-xs text-gray-600">{row.reason}</p>
                    )}
                    {row.restored && (
                      <p className="mt-0.5 text-xs text-violet-700">
                        You cancelled this day by hand — this run puts it back
                      </p>
                    )}
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    {row.kind !== 'skipped' && (
                      <span className="text-xs text-gray-600">
                        {hoursLabel(row.hours)} · {money(row.cost)}
                      </span>
                    )}
                    <Badge variant="outline" className={`text-[11px] ${tag.className}`}>
                      <tag.Icon className="mr-1 h-3 w-3" />
                      {committed ? tag.done : tag.label}
                    </Badge>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="hours-run-close">
            {committed ? 'Done' : 'Cancel'}
          </Button>
          {!committed && (
            <Button
              onClick={onConfirm}
              disabled={busy || (totals.bookings || 0) === 0}
              className="bg-amber-600 hover:bg-amber-700"
              data-testid="hours-run-confirm"
            >
              {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {confirmLabel || `Book ${hoursLabel(totals.hours)} · ${money(totals.cost)}`}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default HoursRunDialog;
